import {
  DishAllergen,
  DishIngredient,
  DishPreference,
  DishTag,
} from "api-types";

import React, { useMemo } from "react";

import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { NotInterestedRounded } from "@mui/icons-material";

import { useTranslation } from "../../../i18n";
import { AllergenChipOutlined } from "../../Chip/Chip";
import { MenuTreeChip } from "../MenuTree/MenuTreeChip";
import { hasAllergenFilters, useFilterMenu } from "../filters";

const SEPARATOR = " · ";

interface DishMetaRowProps {
  matchedAllergens: DishAllergen[];
  matchedIngredients: DishIngredient[];
  matchedMayContainAllergens: DishAllergen[];
  matchedPreferences: DishPreference[];
  matchedRecommendTags: DishTag[];
  matchedNeutralTags: DishTag[];
  optionalIngredientIds?: Set<string>;
  hasMatchingAllergens: boolean;
  showMayContains: boolean;
  /** How many of this dish's own options suit the guest, already worded. */
  optionsSummary?: string;
  /**
   * True when a required group leaves nothing orderable. That is a verdict
   * about whether the guest can eat this at all, so it joins the safety tier
   * rather than the quiet line a neutral count sits on.
   */
  optionsSummaryBlocked?: boolean;
  /**
   * Shows the options summary as a chip rather than on the quiet line. An
   * option listed inside a sheet needs it to carry: the guest is comparing
   * rows there, and a line of small grey text does not say "there is more
   * inside this one" at a glance.
   */
  optionsSummaryAsChip?: boolean;
  /**
   * Names the allergens whatever the menu-wide setting says. A guest who
   * followed "Why?" has asked the question the summary chip was standing in
   * for, so answering it with the same chip again would be a loop.
   */
  forceDetail?: boolean;
  /** Centres the chips, for the drawer header rather than a list row. */
  centred?: boolean;
  caloriesInKcal?: number;
}

/**
 * Everything a row says about itself, in two tiers.
 *
 * Tier one is safety and only safety: allergens, the ingredients standing in
 * for them, and may-contain, so nothing that is not a safety verdict can land
 * between two that are. May-contain sits at the same weight as a confirmed
 * allergen: a weaker claim carrying the same consequence, and the words
 * already say which it is.
 *
 * Tier two is what the guest reads once they know they can eat it: the diet it
 * suits, how many choices it offers, what it costs them, on one quiet line.
 */
export const DishMetaRow = ({
  matchedAllergens,
  matchedIngredients,
  matchedMayContainAllergens,
  matchedPreferences,
  matchedRecommendTags,
  matchedNeutralTags,
  optionalIngredientIds,
  hasMatchingAllergens,
  showMayContains,
  optionsSummary,
  optionsSummaryBlocked = false,
  optionsSummaryAsChip = false,
  forceDetail = false,
  centred = false,
  caloriesInKcal,
}: DishMetaRowProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    palette: { oscar },
  } = theme;
  const filterMenu = useFilterMenu();
  const showDetail = forceDetail || filterMenu.showAllergenDetails;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // A row cannot carry an unbounded list of chips and stay a row.
  const maxSafetyChips = isSmallScreen ? 4 : 5;

  const safetyItems = useMemo(() => {
    const allergens = matchedAllergens.map((allergen) => ({
      key: `allergen:${allergen.igfdTaxAllergenId}`,
      label: allergen.allergenName,
      canOmit: !!allergen.isOptional,
    }));
    const ingredients = matchedIngredients.map((ingredient) => ({
      key: `ingredient:${ingredient.igfdTaxIngredientId}`,
      label: ingredient.ingredientName,
      canOmit:
        optionalIngredientIds?.has(ingredient.igfdTaxIngredientId) ?? false,
    }));
    // Removable first: it is the better news, and the guest can act on it.
    return [...allergens, ...ingredients].sort((a, b) => {
      const removable = Number(b.canOmit) - Number(a.canOmit);
      return removable !== 0 ? removable : a.label.localeCompare(b.label);
    });
  }, [matchedAllergens, matchedIngredients, optionalIngredientIds]);

  const everySafetyItemRemovable =
    safetyItems.length > 0 && safetyItems.every((item) => item.canOmit);

  // Only on a row with no safety verdict of its own. Beside one, "1 of 1
  // options suitable" next to "Not suitable" reads like a contradiction. The
  // caller decides if the tally is chippable; only here is the slot known.
  const hasSafetyChips = safetyItems.length > 0 || showMayContains;
  const summaryChip =
    optionsSummaryAsChip && !hasSafetyChips ? optionsSummary : undefined;

  const quietLine = useMemo(() => {
    const parts: string[] = [];
    // Only with the details on. In summary the guest asked one question, and
    // naming their own filter back at them on every matching row answers a
    // different one.
    if (showDetail) {
      matchedPreferences.forEach((preference) =>
        parts.push(preference.preferenceName)
      );
      [...matchedRecommendTags, ...matchedNeutralTags].forEach((tag) =>
        parts.push(tag.name)
      );
    }
    if (optionsSummary && !optionsSummaryBlocked && !summaryChip)
      parts.push(optionsSummary);
    if (caloriesInKcal && filterMenu.showCalories) {
      parts.push(t("common.calories", { count: caloriesInKcal }));
    }
    return parts;
  }, [
    showDetail,
    matchedPreferences,
    matchedRecommendTags,
    matchedNeutralTags,
    optionsSummary,
    optionsSummaryBlocked,
    summaryChip,
    caloriesInKcal,
    filterMenu.showCalories,
    t,
  ]);

  // A menu row caps its chips and offers "+N more", which the sheet behind it
  // then lists in full. This IS that sheet, so capping here would leave the
  // guest nowhere further to go.
  const visibleSafetyItems = forceDetail
    ? safetyItems
    : safetyItems.slice(0, maxSafetyChips);
  const overflowCount = safetyItems.length - visibleSafetyItems.length;

  // Said whichever filter emptied the group, since it is why the dish cannot be
  // ordered. Only the colour is reserved for allergens.
  const showBlockedSummary =
    !!optionsSummary && optionsSummaryBlocked && !summaryChip;
  const blockedIsRisk = hasAllergenFilters(filterMenu);
  const hasSafety = hasSafetyChips || showBlockedSummary;

  if (!hasSafety && !summaryChip && quietLine.length === 0) return null;

  return (
    <Stack alignItems={centred ? "center" : "stretch"} gap={1} width="100%">
      {/* Never the blocked styling: a blocked tally is not a chip, it is the
          line below. dishOptionsSummary makes the two mutually exclusive. */}
      {summaryChip ? (
        <Stack alignItems="flex-start" direction="row">
          <MenuTreeChip label={summaryChip} />
        </Stack>
      ) : null}

      {showBlockedSummary ? (
        // Above the chips, not among them: "you cannot order this" outranks
        // any one ingredient in it.
        <Stack alignItems="center" direction="row" gap={0.5}>
          <NotInterestedRounded
            fontSize="small"
            sx={{
              color: blockedIsRisk
                ? oscar.light.content.danger
                : oscar.light.content.content1,
            }}
          />
          <Typography
            color={
              blockedIsRisk
                ? oscar.light.content.dangerText
                : oscar.light.content.content1
            }
            variant="labelXSMedium"
          >
            {optionsSummary}
          </Typography>
        </Stack>
      ) : null}

      {safetyItems.length > 0 || showMayContains ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={0.5}
          alignItems="center"
          justifyContent={centred ? "center" : "flex-start"}
        >
          {showDetail ? (
            <>
              {visibleSafetyItems.map((item) => (
                <AllergenChipOutlined
                  key={item.key}
                  allergenName={item.label}
                  canOmit={item.canOmit}
                  link={item.canOmit ? t("dish.canBeRemoved") : undefined}
                />
              ))}
              {/* "+3" alone reads as a number, not as three allergens the
                  row ran out of room for. The drawer behind this row lists
                  every one of them. */}
              {overflowCount > 0 ? (
                <AllergenChipOutlined
                  allergenName={t("dish.moreAllergens", {
                    count: overflowCount,
                  })}
                />
              ) : null}
            </>
          ) : safetyItems.length > 0 ? (
            <AllergenChipOutlined
              allergenName={t("dish.notSuitable")}
              canOmit={everySafetyItemRemovable}
              // Either the better news or the way to find out why. The chip
              // has always offered one of the two; the tiering changed where
              // it sits, not what it says.
              link={
                everySafetyItemRemovable
                  ? t("dish.canBeRemoved")
                  : t("common.why")
              }
            />
          ) : null}

          {showMayContains
            ? showDetail
              ? matchedMayContainAllergens.map((allergen) => (
                  <AllergenChipOutlined
                    key={`may:${allergen.igfdTaxAllergenId}`}
                    mayContain
                    allergenName={t("dish.mayContainNamed", {
                      name: allergen.allergenName,
                    })}
                  />
                ))
              : !hasMatchingAllergens && (
                  <AllergenChipOutlined
                    mayContain
                    allergenName={t("dish.mayNotBeSuitable")}
                    link={t("common.why")}
                  />
                )
            : null}
        </Stack>
      ) : null}

      {quietLine.length > 0 ? (
        <Typography
          color={oscar.light.content.content2Text}
          variant="labelXSMedium"
        >
          {quietLine.join(SEPARATOR)}
        </Typography>
      ) : null}
    </Stack>
  );
};
